import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOnboardingContext } from '../contexts/OnboardingContext'
import { getRouteForStep } from '../utils/onboardingRoutes'

// Re-export the context hook for backward compatibility
export { useOnboardingContext } from '../contexts/OnboardingContext'

// Hook principal para usar el contexto de onboarding
export const useOnboarding = () => {
  return useOnboardingContext()
}

// Re-export specialized hooks
export { useOnboardingStep } from './useOnboardingStep'
export { useStepNavigation } from './useStepNavigation'

// Hook para heartbeat automático en componentes de onboarding
export const useHeartbeat = () => {
  // The heartbeat is now handled automatically by the service
  // This hook is kept for backward compatibility but doesn't do anything
  // as the OnboardingService manages heartbeat internally
}

// Hook para manejar la verificación de reanudación en el login
export const useOnboardingResume = () => {
  const { resumePoint, isLoading, checkResumePoint } = useOnboardingContext()
  const navigate = useNavigate()

  const handleResumeRedirect = useCallback(async () => {
    await checkResumePoint()

    if (resumePoint?.canResume && resumePoint.currentStep) {
      const route = getRouteForStep(resumePoint.currentStep, resumePoint.flow?.variant)
      navigate(route)
      return true
    } else {
      // Onboarding completado o no necesario, ir al dashboard
      navigate('/dashboard')
      return false
    }
  }, [resumePoint, checkResumePoint, navigate])

  return {
    resumeData: resumePoint,
    loading: isLoading,
    checkResume: checkResumePoint,
    handleResumeRedirect,
  }
}

// Hook para analytics de onboarding
export const useOnboardingAnalytics = () => {
  const { analytics, isLoading, error, loadAnalytics } = useOnboardingContext()

  return {
    analytics,
    loading: isLoading,
    error,
    refetch: loadAnalytics,
  }
}

// Hook para verificar si el usuario necesita onboarding (usar en rutas protegidas)
export const useOnboardingGuard = () => {
  const { progress, isInitialized, isLoading, checkResumePoint } =
    useOnboardingContext()
  const navigate = useNavigate()

  const needsOnboarding = progress?.status !== 'completed'

  const checkAndRedirect = useCallback(async () => {
    if (!isInitialized) {
      await checkResumePoint()
    }

    if (needsOnboarding && progress?.currentStepKey) {
      const route = getRouteForStep(progress.currentStepKey, progress?.flow?.variant)
      navigate(route)
    } else if (needsOnboarding) {
      navigate('/onboarding')
    }
  }, [isInitialized, needsOnboarding, progress, navigate, checkResumePoint])

  return {
    needsOnboarding,
    loading: isLoading,
    checkAndRedirect,
  }
}
